import { createTRPCClient, httpLink } from '@trpc/client'
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import { DEFAULT_API_ENDPOINT, getStorage } from 'shared/lib/storage'
import { createTrpcDebugLink, installTrpcExtensionBridge } from 'shared/lib/trpc-debugger'
import { useAuthStore } from 'shared/store/auth'
import superjson from 'superjson'
import type { AdminRouter as AppRouter } from './app-router'
import { queryClient } from './tanstack-query-config'

export type RouterInputs = inferRouterInputs<AppRouter>
export type RouterOutputs = inferRouterOutputs<AppRouter>

const url = getStorage('dev')?.selectedApiEndpoint || DEFAULT_API_ENDPOINT

// Lets the tRPC debugger browser extension observe operations; idle without it.
installTrpcExtensionBridge()

export const trpcClient = createTRPCClient<AppRouter>({
	links: [
		createTrpcDebugLink({ client: 'admin' }),
		httpLink({
			transformer: superjson,
			url: url + '/trpc/admin',
			headers() {
				const token = useAuthStore.getState().token
				return token
					? {
							authorization: `Bearer ${token}`,
						}
					: {}
			},
		}),
	],
})

export const trpc = createTRPCOptionsProxy<AppRouter>({
	client: trpcClient,
	queryClient,
})

/**
 * Hook for accessing tRPC client in React components.
 * Matches the documented pattern in user-docs/trpc-client-usage.md
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const trpc = useTRPC()
 *   const { data } = useQuery(trpc.locations.List.queryOptions())
 * }
 * ```
 */
export function useTRPC() {
	return trpc
}
